import React, { useState, useCallback, useMemo } from 'react';
import {
  approveDraftOrder,
  declineDraftOrder,
  requestUpdates,
  requestExtension,
  askQuestion,
  uploadCompetitorQuote,
} from '../../api/draftOrders';
import { getToken } from '../../utils/auth';
import { API_BASE_URL } from '../../utils/constants';
import { modalConfig, modalFieldDefinitions, PAYMENT_METHOD_GOV_PO } from '../../utils/modalFields';
import ModalField from './ModalField';

// import { pushDataLayerEvent } from '../../utils/utils';

import { IoMdCheckmarkCircleOutline } from "react-icons/io";


// ---------------------------------------------------------------------------
// Pure helpers (module scope so they don't reallocate on every render)
// ---------------------------------------------------------------------------

const coerceBool = (v) => v === true || v === 'true' || v === 1 || v === '1';

const normalizeArray = (v) =>
  Array.isArray(v) ? v : v == null || v === '' ? [] : [v];

const matchesCondition = (cond, formData) => {
  const current = formData?.[cond.field];
  if (Object.prototype.hasOwnProperty.call(cond, 'includes')) {
    return normalizeArray(current).includes(cond.includes);
  }
  if (Object.prototype.hasOwnProperty.call(cond, 'equals')) {
    if (typeof cond.equals === 'boolean') return coerceBool(current) === cond.equals;
    return current === cond.equals;
  }
  if (cond.notEmpty) {
    if (Array.isArray(current)) return current.length > 0;
    return current != null && String(current).trim() !== '';
  }
  return true;
};

const matchesShowWhen = (field, formData) => {
  if (!field) return true;
  if (Array.isArray(field.showWhenAll)) {
    return field.showWhenAll.every((c) => matchesCondition(c, formData));
  }
  if (Array.isArray(field.showWhenAny)) {
    return field.showWhenAny.some((c) => matchesCondition(c, formData));
  }
  if (field.showWhen) return matchesCondition(field.showWhen, formData);
  return true;
};

const formatFieldValue = (field, raw) => {
  if (raw == null) return '';
  if (raw instanceof File) return `[Uploaded file: ${raw.name}]`;
  if (Array.isArray(raw)) {
    if (field.options) {
      const labelMap = Object.fromEntries(
        field.options.map((o) => [o.value, o.shortLabel || o.label || o.value])
      );
      return raw.map((v) => labelMap[v] || v).join(', ');
    }
    return raw.join(', ');
  }
  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
  return String(raw).trim();
};

const buildFieldSummary = (fields, formData) => {
  const lines = [];
  (fields || []).forEach((field) => {
    const label = field.shortLabel || field.label;
    if (!label) return;
    const value = formatFieldValue(field, formData[field.name]);
    if (!value) return;
    lines.push(`${label}: ${value}`);
  });
  return lines.join('\n');
};


function ModalAction({ mode, orderId, onSuccess, title, message, canPayByPo, quoteName, onSecondaryAction }) {
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  const config = modalConfig[mode];

  // const eventName = `quote_portal_${mode}_modal_open`;
  // pushDataLayerEvent(eventName, {
  //   order_id: orderId,
  //   customer_email: customerEmail || undefined,
  //   quote_name: quoteName || undefined,
  // });

  // Memoized field list. We `filter` (not map) so each field keeps its module-
  // level reference identity, which lets React.memo'd ModalField bail out on
  // unrelated re-renders.
  const fields = useMemo(() => {
    if (!config) return [];
    const includePo =
      mode === 'approve' &&
      (canPayByPo || formData.paymentMethod === PAYMENT_METHOD_GOV_PO);
    const rawFields = includePo
      ? [...(config.fields || []), ...modalFieldDefinitions.approvePo]
      : config.fields || [];
    return rawFields.filter((field) => matchesShowWhen(field, formData));
  }, [config, mode, canPayByPo, formData]);

  const handleFieldChange = useCallback((name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormErrors(prev => {
      if (prev[name]) {
        const { [name]: _, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const allRequiredFilled = useMemo(() => {
    return fields.every(field => {
      if (!field.required) return true;
      const val = formData[field.name];
      if (field.type === 'switch') return val === true || val === 'true';
      if (field.type === 'checkbox') return Array.isArray(val) && val.length > 0;
      return val != null && String(val).trim() !== '';
    });
  }, [fields, formData]);

  const handleSecondaryAction = useCallback(async () => {
    // If parent provided a custom handler (e.g. open another modal), prefer that.
    if (onSecondaryAction) {
      onSecondaryAction();
      return;
    }

    // Fallback: original approve secondary behavior
    if (mode !== 'approve') return;

    setIsProcessing(true);
    const { token } = getToken();
    const updateRequest = formData.text || 'Customer requested updates';

    try {
      await requestUpdates(orderId, updateRequest, token);
      onSuccess('updates-requested');
    } catch (error) {
      console.error('Failed to request updates:', error);
      alert(`Failed to request updates: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  }, [mode, orderId, formData.text, onSuccess, onSecondaryAction]);

  const validateForm = () => {
    const errors = {};

    fields.forEach(field => {
      if (!field.required) return;
      const val = formData[field.name];
      const empty =
        val === undefined ||
        val === null ||
        (typeof val === 'string' && !val.trim()) ||
        (Array.isArray(val) && val.length === 0) ||
        (field.type === 'file' && !(val instanceof File));
      if (empty) errors[field.name] = 'This field is required';
    });

    // When paying by PO: if PO number is entered, file is required
    // if (mode === 'approve' && canPayByPo && formData.poNumber?.trim() && !(formData.poFile instanceof File)) {
    //   errors.poFile = 'Please upload your purchase order document when paying by PO.';
    // }

    // MAKES PO FILE UPLOAD REQUIRED
    // if (
    //   mode === 'approve' &&
    //   (canPayByPo || formData.paymentMethod === PAYMENT_METHOD_GOV_PO) &&
    //   formData.poNumber?.trim() &&
    //   !(formData.poFile instanceof File)
    // ) {
    //   errors.poFile = 'Please upload your purchase order document.';
    // }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  

  const handlePrimaryAction = async () => {
    if (!validateForm()) return;

    setIsProcessing(true);
    const { token } = getToken();
    const { text } = formData;

    try {
      switch (mode) {
        case 'approve': {
          const summary = buildFieldSummary(fields, formData);
          const body = summary || text || '';
          const result = await approveDraftOrder(orderId, body, token, formData.poFile || undefined);
          onSuccess('approved', result);
          break;
        }
        case 'decline': {
          const summary = buildFieldSummary(fields, formData);
          const declineMessage = summary || text?.trim() || 'No reason provided';

          // Optional competitor quote upload when user has attached a file
          const competitorFile = formData.declineCheaperElsewhereQuoteFile;
          if (competitorFile instanceof File) {
            try {
              await uploadCompetitorQuote(orderId, token, competitorFile);
            } catch (uploadError) {
              console.error('Failed to upload competitor quote:', uploadError);
              // Non‑blocking: still proceed with decline, just inform the user
              alert('We could not upload the competitor quote file, but your decline reason was still submitted.');
            }
          }

          await declineDraftOrder(orderId, declineMessage, token);
          onSuccess();
          break;
        }
        case 'requestUpdates': {
          const summary = buildFieldSummary(fields, formData);
          const updateMessage = summary || text?.trim() || 'Customer requested updates';

           // If customer attached a tax certificate in the update modal, upload it
          // and associate it with the customer (and this draft order) before
          // sending the update request. Best‑effort: failures are logged but do
          // not block the main request.
          const taxCertFile = formData.taxCertificate;
          if (taxCertFile instanceof File) {
            try {
              const formDataUpload = new FormData();
              formDataUpload.append('taxCertFile', taxCertFile);
              formDataUpload.append('token', token);
              formDataUpload.append('draftOrderId', orderId);

              const uploadResponse = await fetch(`${API_BASE_URL}/api/customer/tax-cert`, {
                method: 'POST',
                body: formDataUpload,
              });

              if (!uploadResponse.ok) {
                const uploadErrorBody = await uploadResponse.json().catch(() => ({}));
                console.error('Tax certificate upload from update modal failed:', uploadErrorBody);
              }
            } catch (uploadErr) {
              console.error('Tax certificate upload from update modal error:', uploadErr);
            }
          }


          await requestUpdates(orderId, updateMessage, token);
          onSuccess();
          break;
        }
        case 'requestExtension': {
          const summary = buildFieldSummary(fields, formData);
          const body = summary || text || '';
          await requestExtension(orderId, body, token);
          onSuccess();
          break;
        }
        case 'askQuestion': {
          const summary = buildFieldSummary(fields, formData);
          const body = summary || text || '';
          await askQuestion(orderId, body, token);
          onSuccess();
          break;
        }
      }
    } catch (error) {
      console.error(`Failed to ${mode} quote:`, error);
      alert(`Failed to ${mode} quote: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  

  // Success mode
  if (mode === 'success') {
    return (
      <div className="modal-success">
        <div className="modal-success-icon">
          <IoMdCheckmarkCircleOutline className="icon-large" />
        </div>
        <h3>{title}</h3>
        <p>{message}</p>
      </div>
    );
  }

  return (
    <div className="modal-inner-wrapper">
      {config.description && (
        <div className='modal-description' dangerouslySetInnerHTML={{ __html: config.description }} />
      )}

      <div className="modal-fields">
        {/* Render fields dynamically from config (includes PO fields when canPayByPo) */}
        {fields.map((field) => (
          <ModalField
            key={field.name}
            field={field}
            value={formData[field.name]}
            onChange={handleFieldChange}
            error={formErrors[field.name]}
          />
        ))}
      </div>

      <div className={`modal-actions ${config.showSecondary ? 'modal-actions--split' : ''}`}>

        <button
          className={`button ${config.buttonClass || 'button--primary button--bordered'}`}
          onClick={handlePrimaryAction}
          disabled={isProcessing || !allRequiredFilled}
        >
          {isProcessing ? 'Processing...' : config.primaryButton}
        </button>
        {config.showSecondary && (
          <button
            className="button button--secondary button--bordered"
            onClick={handleSecondaryAction}
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : config.secondaryButton}
          </button>
        )}
      </div>
    </div>
  );
}

export default ModalAction;
